import { createI18n } from 'vue-i18n'
import en from './en.json'
import { PACKS } from '@/marketing/i18n'

const STORAGE_KEY = 'signyt:locale'

const BUNDLED = ['en', 'pl', 'uk', 'de', 'es'] as const
type Bundled = (typeof BUNDLED)[number]
const isBundled = (v: string | null): v is Bundled => BUNDLED.includes(v as Bundled)

function detectInitialLocale(): Bundled {
  // 1. Persisted user choice wins
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isBundled(saved)) return saved
  } catch { /* SSR / locked storage */ }
  // 2. Browser preference (first language, e.g. "pl-PL" -> "pl")
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2).toLowerCase() : ''
  return isBundled(nav) ? nav : 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: detectInitialLocale(),
  fallbackLocale: 'en',
  messages: { en }, // other bundled locales load on demand (code-split)
  // The legal pages (Privacy / Terms / DPA) embed <a> tags inside i18n
  // strings and render them with v-html — that's a deliberate trade-off
  // (single source of truth per locale, no per-paragraph slot machinery)
  // and the strings are operator-controlled, not user input. Silence the
  // generic XSS advisory so the console stays useful for actual bugs.
  warnHtmlMessage: false,
  warnHtmlInMessage: 'off',
})

// Bundled locales other than `en` are separate chunks, fetched on demand.
const LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  pl: () => import('./pl.json'),
  uk: () => import('./uk.json'),
  de: () => import('./de.json'),
  es: () => import('./es.json'),
}

/** Ensure bundled messages for `loc` are registered (no-op for en/server-only). */
export async function loadLocaleMessages(loc: string): Promise<void> {
  const load = LOADERS[loc]
  if (!load || i18n.global.availableLocales.includes(loc as never)) return
  try {
    const msgs = (await load()).default
    // Landing copy (home.*) for de/es/uk is maintained with the marketing packs;
    // applied before server overrides (refreshServerTranslations loads first).
    const pack = (PACKS as Record<string, { home: Record<string, string> } | undefined>)[loc]
    if (pack) msgs.home = { ...(msgs.home as Record<string, string>), ...pack.home }
    i18n.global.setLocaleMessage(loc, msgs as never)
  } catch { /* offline: fall back to en until next switch */ }
}

/** Saved user pick, if any. */
export function savedLocale(): string | null {
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}

// persist=false: URL-driven switch (marketing), not the user's pick.
export async function setLocale(locale: string, persist = true) {
  await loadLocaleMessages(locale)
  // @ts-expect-error — Composer locale is a Ref in the setup API
  i18n.global.locale.value = locale
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch { /* silent */ }
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
  }
}

// Built-in locales shown in the switcher by default. Server-registered locales
// (via super admin) get appended to this at runtime by `refreshServerTranslations`.
export const BUILTIN_LOCALES: { code: string; label: string; flag_code: string }[] = [
  { code: 'en', label: 'English', flag_code: 'gb' },
  { code: 'pl', label: 'Polski', flag_code: 'pl' },
  { code: 'uk', label: 'Українська', flag_code: 'ua' },
  { code: 'de', label: 'Deutsch', flag_code: 'de' },
  { code: 'es', label: 'Español', flag_code: 'es' },
]

import { ref } from 'vue'
export const availableLocales = ref<{ code: string; label: string; flag_code: string }[]>(
  [...BUILTIN_LOCALES],
)
// Backwards-compat alias used in a few places
export const AVAILABLE_LOCALES = BUILTIN_LOCALES

// Deep-merge helper — used to overlay server overrides on top of bundled JSON.
function deepSet(target: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.')
  let cur = target
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {}
    cur = cur[k] as Record<string, unknown>
  }
  cur[parts[parts.length - 1]] = value
}

// Language code → human label fallback, used when we see a new server locale.
const _LOCALE_NAMES: Record<string, string> = {
  en: 'English', pl: 'Polski', de: 'Deutsch', fr: 'Français',
  es: 'Español', it: 'Italiano', uk: 'Українська', cs: 'Čeština',
  sk: 'Slovenčina', ro: 'Română', hu: 'Magyar', nl: 'Nederlands',
  pt: 'Português', ru: 'Русский',
}
const _LOCALE_FLAGS: Record<string, string> = {
  en: 'gb', pl: 'pl', de: 'de', fr: 'fr', es: 'es', it: 'it',
  uk: 'ua', cs: 'cz', sk: 'sk', ro: 'ro', hu: 'hu', nl: 'nl',
  pt: 'pt', ru: 'ru',
}

/** Fetch server-side translation overrides and merge into i18n messages. */
export async function refreshServerTranslations(fetcher: () => Promise<Record<string, Record<string, string>>>) {
  try {
    const serverMap = await fetcher()
    const newLocales: string[] = []
    for (const [loc, kv] of Object.entries(serverMap)) {
      await loadLocaleMessages(loc)
      // If the server references an unknown locale, seed it from EN messages
      if (!i18n.global.availableLocales.includes(loc as never)) {
        i18n.global.setLocaleMessage(loc, JSON.parse(JSON.stringify(en)))
        newLocales.push(loc)
      }
      // Grab the existing messages object, overlay each dotted key
      const current = JSON.parse(JSON.stringify(i18n.global.getLocaleMessage(loc) ?? {}))
      for (const [key, val] of Object.entries(kv)) {
        if (val && String(val).trim().length > 0) deepSet(current, key, val)
      }
      i18n.global.setLocaleMessage(loc, current)
    }
    // Extend the switcher list for any brand-new server-only locales
    for (const loc of newLocales) {
      if (!availableLocales.value.some(l => l.code === loc)) {
        availableLocales.value.push({
          code: loc,
          label: _LOCALE_NAMES[loc] ?? loc.toUpperCase(),
          flag_code: _LOCALE_FLAGS[loc] ?? loc.slice(0, 2).toLowerCase(),
        })
      }
    }
  } catch {
    // Silent — missing /translations shouldn't break the app
  }
}
